import { clearNativePersistence } from '~/utils/nativePersistence'
import type { Fb } from '~/composables/useFirebase'
import { ACCOUNT_DELETION_SESSION, clearAccountStorage } from '~/utils/accountDeletion'

// This tab-only cleanup receipt deliberately survives the game-key sweep. A
// pagehide save writer may run during navigation, so the isolated landing page
// sweeps once more before any game code can load those old saves again.
const PENDING_WIPE = 'ed-account-deleted-pending-wipe'

function clearDeviceStorage(): boolean {
  let cleared = true
  for (const storage of [() => localStorage, () => sessionStorage]) {
    try { clearAccountStorage(storage()) } catch { cleared = false }
  }
  return cleared
}

function receipt(): string | null {
  try { return sessionStorage.getItem(PENDING_WIPE) } catch { return null }
}

function saveReceipt(value: string): boolean {
  try { sessionStorage.setItem(PENDING_WIPE, value); return receipt() === value } catch { return false }
}

function tokenUid(token: unknown): string | undefined {
  try {
    if (typeof token !== 'string') return
    const payload = token.split('.')[1]
    const uid = payload && JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).sub
    return typeof uid === 'string' ? uid : undefined
  } catch { return }
}

/** Firebase swallows an invalid persisted user before its first auth callback.
 * Observe only its signed-in lookup/refresh requests and exact USER_NOT_FOUND
 * response. The SDK maps that response to user-token-expired, which on its own
 * also means ordinary revocation and must never erase a player's saves. */
export default defineNuxtPlugin({
  name: 'account-deleted',
  enforce: 'pre',
  async setup(nuxtApp) {
    const { t } = useLocale()
    async function recoverCleanup(needsReceipt: boolean) {
      // Also works in the current page if storage cannot retain a receipt for
      // navigation. Never claim success while Auth or a key sweep still fails.
      const notice = document.createElement('div')
      notice.setAttribute('style', 'position:fixed;inset:0;z-index:2147483647;background:#fff7f2;color:#202d3c;display:grid;place-content:center;gap:16px;padding:24px;text-align:center')
      const line = document.createElement('p')
      line.setAttribute('role', 'status')
      line.textContent = t('account.deletedCleanupPending')
      const button = document.createElement('button')
      button.textContent = t('account.retryCleanup')
      notice.append(line, button)
      document.body.append(notice)
      await new Promise<void>((resolve) => {
        let busy = false
        const retry = async () => {
          if (busy) return
          busy = button.disabled = true
          try {
            const fb = await bootFirebase()
            await fb.authMod.signOut(fb.auth)
            await clearNativePersistence({ accountDeleted: true })
            if (!clearDeviceStorage() || needsReceipt && !saveReceipt('signed-out')) return
            window.removeEventListener('focus', retry)
            window.removeEventListener('online', retry)
            notice.remove()
            resolve()
          } catch { /* keep the receipt and the explicit retry available */ }
          finally { busy = button.disabled = false }
        }
        button.addEventListener('click', retry)
        window.addEventListener('focus', retry)
        window.addEventListener('online', retry)
        void retry()
      })
    }

    const pending = receipt()
    if (pending) {
      let nativeCleared = true
      try { await clearNativePersistence({ accountDeleted: true }) } catch { nativeCleared = false }
      const cleared = clearDeviceStorage() && nativeCleared
      if (!ACCOUNT_DELETION_SESSION) {
        window.location.replace('/delete-account?deleted=1')
        // Keep the receipt and hold other plugins until the isolated page loads.
        return new Promise<void>(() => {})
      }
      if (pending !== 'signed-out' || !cleared) {
        // A failed Auth-persistence cleanup never returns to live gameplay or
        // displays success. This isolated page can retry without save writers.
        await recoverCleanup(false)
      }
      // The success page itself never boots Firebase or recreates a guest.
      clearDeviceStorage()
      try { sessionStorage.removeItem(PENDING_WIPE) } catch { /* no storage */ }
      return
    }
    let firebase: Fb | undefined
    let deleting = false
    const ready = firebaseReady().then((fb) => {
      firebase = fb
      // Stop a delayed wallet mint or sign-in from replacing the erased user
      // while sign-out and the full page navigation are still in progress.
      fb.authMod.beforeAuthStateChanged(fb.auth, (next) => {
        if (deleting && next) throw new Error('Account deleted')
      })
      return fb
    })

    function deleted(uid?: string) {
      if (deleting) return
      const current = firebase?.auth.currentUser
      if (uid && current && current.uid !== uid) return
      deleting = true
      saveReceipt('pending')
      clearDeviceStorage()
      // Block late native mirrors immediately; the awaited retry below handles
      // a transient Preferences failure without restoring deleted progress.
      void clearNativePersistence({ accountDeleted: true }).catch(() => {})
      // Registered at the point of deletion, after the normal save listeners.
      window.addEventListener('pagehide', clearDeviceStorage)
      void bootFirebase().then(async () => {
        const fb = await ready
        // Use Firebase directly: the normal sign-out action ships the run and
        // wallet outbox, which a deleted account must never replay.
        await fb.authMod.signOut(fb.auth)
        await clearNativePersistence({ accountDeleted: true })
        if (!clearDeviceStorage() || !saveReceipt('signed-out')) throw new Error('Device storage cleanup unavailable')
      }).catch(async (error) => {
        console.warn('Deleted-account sign-out will retry on the isolated cleanup page.', error)
        // If no receipt survived, retry here: navigating could otherwise show a
        // successful deletion page with no way to discover incomplete cleanup.
        if (!receipt()) await recoverCleanup(true)
      }).finally(() => {
        clearDeviceStorage()
        window.location.replace('/delete-account?deleted=1')
      })
    }

    window.addEventListener('elemental-dispatch-account-deleted', (event) => {
      deleted((event as CustomEvent<{ uid?: string }>).detail?.uid)
    })

    const missingUser = (error: unknown) => {
      if ((error as { code?: string } | null)?.code !== 'auth/user-not-found') return
      const user = firebase?.auth.currentUser
      if (!user) return
      // An attempted sign-in for a different account can report the same code.
      // Verify the currently signed-in user before deleting this device's data.
      void user.reload().catch((failure: unknown) => {
        if ((failure as { code?: string } | null)?.code === 'auth/user-not-found') deleted(user.uid)
      })
    }
    window.addEventListener('unhandledrejection', (event) => missingUser((event as PromiseRejectionEvent).reason))
    nuxtApp.hook('vue:error', missingUser)
    nuxtApp.hook('app:error', missingUser)

    const fetch = window.fetch.bind(window)
    window.fetch = async (input, init) => {
      let endpoint: URL | undefined
      try {
        const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url)
        if (url.protocol === 'https:' && (
          url.hostname === 'identitytoolkit.googleapis.com' && url.pathname === '/v1/accounts:lookup'
          || url.hostname === 'securetoken.googleapis.com' && url.pathname === '/v1/token'
        )) endpoint = url
      } catch { /* relative game fetch or unrelated request */ }
      const requestUser = firebase?.auth.currentUser
      const body = endpoint && (typeof init?.body === 'string' ? init.body
        : input instanceof Request ? input.clone().text() : undefined)
      const response = await fetch(input, init)
      if (endpoint && !response.ok) {
        // Inspect before returning, so Auth cannot first discard the user and
        // let the wallet mint a replacement guest. Do not await sign-out here:
        // initialization needs this response to finish. The SDK body is intact.
        await (async () => {
          const failure = await response.clone().json() as { error?: { message?: string } }
          if (failure.error?.message !== 'USER_NOT_FOUND') return
          const fb = await ready
          if (endpoint.searchParams.get('key') !== fb.app.options.apiKey) return
          const raw = await body
          if (typeof raw !== 'string') return
          let uid = requestUser?.uid
          if (endpoint.pathname === '/v1/accounts:lookup') {
            uid = tokenUid((JSON.parse(raw) as { idToken?: unknown }).idToken)
            if (!uid) return // not a signed-in account lookup
          } else {
            const fields = new URLSearchParams(raw)
            if (fields.get('grant_type') !== 'refresh_token' || !fields.get('refresh_token')) return
            // A detached old User can still refresh after another account signs
            // in. The current UID alone does not identify that request's token.
            if (requestUser && fields.get('refresh_token') !== requestUser.refreshToken) return
            // During startup Auth has not published the persisted user yet. A
            // later, different sign-in must not be erased by that old response.
            if (!uid && fb.auth.currentUser) return
          }
          deleted(uid)
        })().catch(() => { /* malformed/unreadable responses are not deletion proof */ })
      }
      return response
    }
  },
})
